import React, { useEffect, useState } from 'react';
import {
  ForwardedOrderInfo,
  OrderLabelResponse,
} from '../../../types/orderTypes';
import dayjs from 'dayjs';
import { formatPhoneNumber } from '../../../utils/formatting';
import {
  confirmOneHDOrderManual,
  fetchForwardedOrderLabels,
} from '../../../fetch/orderFetch';
import Button from '../../../components/buttons/Button';
import Swal from 'sweetalert2';

export interface ForwardedOrderInfoComponentProps {
  forwardedOrderInfo: ForwardedOrderInfo;
}

export const ForwardedOrderInfoComponent = ({
  forwardedOrderInfo,
}: ForwardedOrderInfoComponentProps): JSX.Element => {
  const [orderLabelInfo, setOrderLabelInfo] = useState<OrderLabelResponse>();

  const fetchLabelInfo = async (orderId: string) => {
    const labelInfo = await fetchForwardedOrderLabels(orderId);
    setOrderLabelInfo(labelInfo);
  };

  useEffect(() => {
    fetchLabelInfo(forwardedOrderInfo.orderId);
  }, [forwardedOrderInfo]);

  const confirmOrderManually = async () => {
    const result = await Swal.fire({
      title: 'Confirm OneHD order',
      text: `Do you want to confirm order ${forwardedOrderInfo.orderId} manuallY? Please make sure the order has not been confirmed by the merchant already!`,
      icon: 'question',
      showCancelButton: true,
    });
    if (result.isConfirmed) {
      const result = await confirmOneHDOrderManual(forwardedOrderInfo.orderId);
      if (result) {
        Swal.fire({
          title: 'Order confirmed successfully!',
          icon: 'success',
        }).then();
      } else {
        Swal.fire({
          title: 'Error occured when confirming order!',
          text: 'Please check back with tech for support.',
          icon: 'error',
        }).then();
      }
    }
  };

  const deliveryDate = forwardedOrderInfo.deliverFrom
    ? dayjs(forwardedOrderInfo.deliverFrom).format('DD.MM.YY')
    : '-';

  const interval =
    forwardedOrderInfo.deliverFrom && forwardedOrderInfo.deliverUntil
      ? `${dayjs(forwardedOrderInfo.deliverFrom).format('HH:mm')} - ${dayjs(
          forwardedOrderInfo.deliverUntil
        ).format('HH:mm')}`
      : '-';

  let specialWindow: string = undefined;
  if (forwardedOrderInfo.consolidated) {
    specialWindow = 'Consolidated';
  } else if (forwardedOrderInfo.bestAvailable) {
    specialWindow = 'Best Available';
  }

  const street = forwardedOrderInfo.recipientAddress.streetNumber
    ? `${forwardedOrderInfo.recipientAddress.streetName} ${forwardedOrderInfo.recipientAddress.streetNumber}`
    : forwardedOrderInfo.recipientAddress.streetName;

  const address = `${street}, ${forwardedOrderInfo.recipientAddress.postalCode} ${forwardedOrderInfo.recipientAddress.city}, ${forwardedOrderInfo.recipientAddress.country}`;

  return (
    <div className="order">
      <h1 style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 24 }}>
        Order {forwardedOrderInfo.orderId} was forwarded to Carriers
      </h1>
      <div style={{ height: 10 }} />
      <p style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 18 }}>
        For further information and handling, please check in carriers ui!
      </p>
      <div style={{ height: 20 }} />
      <div style={{ textAlign: 'center', fontSize: 18 }}>
        Summary of order request
      </div>
      <div className="order__information">
        <div className="information__box">
          <span className="box__field">
            <h5>Order ID:</h5>
            <h5>{forwardedOrderInfo.orderId}</h5>
          </span>
          <span className="box__field">
            <h5>Received at:</h5>
            <h5>
              {dayjs(forwardedOrderInfo.requestReceivedAt).format(
                'HH:mm DD.MM.YY'
              )}
            </h5>
          </span>
          <span className="box__field">
            <h5>Order Reference:</h5>
            <h5>{forwardedOrderInfo.orderReference || '-'}</h5>
          </span>
        </div>
        <div className="information__box">
          <span className="box__field">
            <h5>Delivery Date:</h5>
            <h5>{deliveryDate}</h5>
          </span>
          <span className="box__field">
            <h5>Delivery Window:</h5>
            <h5>{interval}</h5>
          </span>
          {specialWindow && (
            <span className="box__field">
              <h5>{specialWindow}</h5>
            </span>
          )}
        </div>
        <div className="information__box">
          <span className="box__field">
            <h5>Account:</h5>
            <h5>{forwardedOrderInfo.accountName}</h5>
          </span>
          <span className="box__field">
            <h5>Sender Name:</h5>
            <h5>{forwardedOrderInfo.pickupName}</h5>
          </span>
          <span className="box__field">
            <h5>Parcel Count:</h5>
            <h5>{forwardedOrderInfo.parcelCount}</h5>
          </span>
        </div>
      </div>
      <div style={{ height: 20 }} />
      <div className="order__information">
        <div className="order__address">
          <h5>Recipient</h5>
          <div className="address-line">
            <span>Name:</span>
            <span>{forwardedOrderInfo.recipientName}</span>
          </div>
          <div className="address-line">
            <span>Email:</span>
            <span>{forwardedOrderInfo.recipientEmail}</span>
          </div>
          <div className="address-line">
            <span>Address:</span>
            <span>{address}</span>
          </div>
          <div className="address-line">
            <span>Phone:</span>
            <span>
              {formatPhoneNumber(
                `${forwardedOrderInfo.recipientPhoneCountryCode}${forwardedOrderInfo.recipientPhoneNumber}`
              )}
            </span>
          </div>
        </div>
      </div>
      <div style={{ height: 20 }} />
      <div className="order__information">
        <div className="information__box">
          {orderLabelInfo?.shipmentLabelUrl && (
            <a
              href={orderLabelInfo.shipmentLabelUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Print label
            </a>
          )}
        </div>
        <div className="information__box">
          <Button
            title="Confirm Order"
            onClick={() => confirmOrderManually()}
          />
        </div>
      </div>
    </div>
  );
};
